import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Leaf,
  DollarSign,
  Users,
  TrendingUp,
  Copy,
  Link,
  CheckCircle,
  Clock,
  LogOut
} from 'lucide-react';

const ReferralPortalPage = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [commissions, setCommissions] = useState([]);
  
  useEffect(() => {
    if (user?.role !== 'referral_broker') {
      // Redirect non-referral brokers
      window.location.href = '/dashboard';
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [dashboardRes, leadsRes, commissionsRes] = await Promise.all([
        api.get('/portal/dashboard'),
        api.get('/portal/leads'),
        api.get('/portal/commissions')
      ]);
      
      setDashboard(dashboardRes.data);
      setLeads(leadsRes.data);
      setCommissions(commissionsRes.data);
    } catch (error) {
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (dashboard?.referral_link) {
      navigator.clipboard.writeText(dashboard.referral_link);
      toast.success('Referral link copied to clipboard');
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-blue-500"><CheckCircle className="mr-1 h-3 w-3" />Approved</Badge>;
      case 'paid':
        return <Badge className="bg-green-500"><CheckCircle className="mr-1 h-3 w-3" />Paid</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Unable to load portal data</p>
            <Button onClick={handleLogout} className="mt-4">Logout</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { broker, stats, referral_link } = dashboard;

  return (
    <div className="min-h-screen bg-background" data-testid="referral-portal-page">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <Leaf className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg">Great Leaf Lending</h1>
              <p className="text-xs text-muted-foreground">Referral Partner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Welcome, {broker.name}
            </span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Referral Link Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Your Referral Link
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Share this link to track leads referred by you
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Input 
                  value={referral_link} 
                  readOnly 
                  className="w-[400px] font-mono text-sm"
                />
                <Button onClick={copyReferralLink} data-testid="copy-referral-link">
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Link
                </Button>
              </div>
            </div>
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <strong>Your Referral Code:</strong>{' '}
                <code className="bg-background px-2 py-1 rounded font-mono">{broker.referral_code}</code>
                <span className="text-muted-foreground ml-4">
                  Commission: {broker.commission_type === 'percentage' ? `${broker.commission_value}%` : `$${broker.commission_value}`}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-3xl font-bold">{stats.total_leads}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Deals Closed</p>
                  <p className="text-3xl font-bold">{stats.total_deals_closed}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {stats.conversion_rate}% conversion
              </p>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${stats.total_commission_earned?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <DollarSign className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Payout</p>
                  <p className="text-3xl font-bold text-amber-600">
                    ${stats.total_commission_pending?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-xl">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leads Table */}
          <Card>
            <CardHeader>
              <CardTitle>Your Referred Leads</CardTitle>
              <CardDescription>Contacts referred through your link</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Loan Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.length > 0 ? (
                      leads.map((lead) => (
                        <TableRow key={lead.contact_id}>
                          <TableCell className="font-medium">
                            {lead.first_name} {lead.last_name}
                          </TableCell>
                          <TableCell>
                            {lead.loan_types?.join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{lead.current_stage}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No leads yet. Share your referral link to get started!
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Commissions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Commission History</CardTitle>
              <CardDescription>Your earned commissions from closed deals</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.length > 0 ? (
                      commissions.map((commission) => (
                        <TableRow key={commission.commission_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {commission.contact?.first_name} {commission.contact?.last_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {commission.calculation_note}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            ${commission.amount?.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(commission.status)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No commissions yet. Commissions are earned when your referrals close deals.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ReferralPortalPage;
